import { supabase } from '../lib/supabase';

export interface DocumentUploadResult {
  success: boolean;
  documentUrl?: string;
  documentPath?: string;
  documentData?: {
    filename: string;
    size: number;
    mimeType: string;
    extension: string;
  };
  error?: string;
}

export class DocumentUploadService {
  private static readonly SUPPORTED_TYPES = [
    // Microsoft Office
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/msword', // .doc
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.ms-excel', // .xls
    
    // PDF
    'application/pdf',
    
    // Text files
    'text/plain', // .txt
    'text/csv', // .csv
    'application/csv', // .csv
    
    // OpenDocument
    'application/vnd.oasis.opendocument.text', // .odt
    'application/vnd.oasis.opendocument.presentation', // .odp
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    
    // Other common formats
    'application/rtf', // .rtf
    'application/json', // .json
    'application/xml', // .xml
  ];

  private static readonly MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  /**
   * Validate if file is a supported document type
   */
  static isSupportedDocumentType(file: File): boolean {
    return this.SUPPORTED_TYPES.includes(file.type.toLowerCase());
  }

  /**
   * Get file extension from filename
   */
  static getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Get document type category
   */
  static getDocumentCategory(mimeType: string): string {
    if (mimeType.includes('word') || mimeType.includes('text')) return 'document';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return 'presentation';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
    if (mimeType.includes('pdf')) return 'pdf';
    if (mimeType.includes('csv')) return 'data';
    return 'document';
  }

  /**
   * Get document icon based on file type
   */
  static getDocumentIcon(mimeType: string): string {
    const category = this.getDocumentCategory(mimeType);
    switch (category) {
      case 'document': return '📄';
      case 'presentation': return '📊';
      case 'spreadsheet': return '📈';
      case 'pdf': return '📕';
      case 'data': return '📋';
      default: return '📄';
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate document path for storage
   */
  static generateDocumentPath(userId: string, messageId: string, filename: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const extension = this.getFileExtension(filename);
    const baseName = filename.replace(/\.[^/.]+$/, '');
    const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_');
    return `documents/${userId}/${timestamp}/${messageId}_${sanitizedBaseName}.${extension}`;
  }

  /**
   * Upload document to Supabase storage
   */
  static async uploadDocument(
    file: File,
    userId: string,
    messageId: string
  ): Promise<DocumentUploadResult> {
    try {
      console.log('Starting document upload process...');

      // Validate file type
      if (!this.isSupportedDocumentType(file)) {
        return {
          success: false,
          error: `Unsupported file type: ${file.type}. Supported types: Word, PowerPoint, Excel, PDF, CSV, and other office documents.`
        };
      }

      // Validate file size
      if (file.size > this.MAX_FILE_SIZE) {
        return {
          success: false,
          error: `File too large. Maximum size is ${this.formatFileSize(this.MAX_FILE_SIZE)}.`
        };
      }

      // Generate document path
      const documentPath = this.generateDocumentPath(userId, messageId, file.name);
      console.log('Generated document path:', documentPath);

      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('message-documents')
        .upload(documentPath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Document upload error:', uploadError);
        
        if (uploadError.message.includes('Bucket not found')) {
          return {
            success: false,
            error: 'Document storage bucket not found. Please contact support.'
          };
        }
        
        return {
          success: false,
          error: `Upload failed: ${uploadError.message}`
        };
      }

      console.log('Document uploaded successfully:', uploadData);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('message-documents')
        .getPublicUrl(documentPath);

      if (!urlData?.publicUrl) {
        return {
          success: false,
          error: 'Failed to generate public URL for document'
        };
      }

      console.log('Document public URL generated:', urlData.publicUrl);

      return {
        success: true,
        documentUrl: urlData.publicUrl,
        documentPath: documentPath,
        documentData: {
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          extension: this.getFileExtension(file.name)
        }
      };

    } catch (error) {
      console.error('Document upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Delete document from storage
   */
  static async deleteDocument(documentPath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from('message-documents')
        .remove([documentPath]);

      if (error) {
        console.error('Document deletion error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Document deletion error:', error);
      return false;
    }
  }

  /**
   * Get supported file extensions
   */
  static getSupportedExtensions(): string[] {
    return [
      '.docx', '.doc', // Word
      '.pptx', '.ppt', // PowerPoint
      '.xlsx', '.xls', // Excel
      '.pdf', // PDF
      '.csv', '.txt', // Text/Data
      '.odt', '.odp', '.ods', // OpenDocument
      '.rtf', '.json', '.xml' // Other
    ];
  }

  /**
   * Get file type description
   */
  static getFileTypeDescription(mimeType: string): string {
    const category = this.getDocumentCategory(mimeType);
    switch (category) {
      case 'document': return 'Word Document';
      case 'presentation': return 'PowerPoint Presentation';
      case 'spreadsheet': return 'Excel Spreadsheet';
      case 'pdf': return 'PDF Document';
      case 'data': return 'Data File';
      default: return 'Document';
    }
  }
}
